import { Panel } from "../../components/panels/Panel";
import { Bar, Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, TimeScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import { useEffect, useMemo, useState } from "react";
import { getInfluxdbData } from "../../services/api/influxdb-tools";
import { ErgonomicsFilters } from "./components/ErgonomicsFilters";
import { ErgonomicsNotifications } from "./components/ErgonomicsNotifications";
import { useOrgData } from "../../contexts/OrgDataContext";
import { useGetData } from "../../services/api/api-tools";
import { Loader } from "../../components/loading/Loader";
import {
  getBarColor,
  getExpositionColor,
  REBA_THRESHOLDS,
  createDateRange,
  REBA_FIELDS, getQuery, formatSeriesByField, getExpositionQuery, REBA_THRESHOLDS_INFLUXDB,
  createPhases
} from "../../services/live-interface/ergonomics-service";
import { useLocation } from "react-router-dom";

ChartJS.register(TimeScale, LinearScale, BarElement, Tooltip, Legend, zoomPlugin, annotationPlugin);

const now = new Date();
const nextHour = new Date(now);
nextHour.setMinutes(0);
nextHour.setSeconds(0);
nextHour.setMilliseconds(0);
nextHour.setHours(nextHour.getHours() + 1);
const today = new Date(nextHour);
const yesterday = new Date(nextHour);
yesterday.setDate(yesterday.getDate() - 1);
const week = new Date(nextHour);
week.setDate(week.getDate() - 7);
const month = new Date(nextHour);
month.setMonth(month.getMonth() - 1);

const defaultDateRange = {
  id: 1,
  //start: yesterday.getTime(),
  start: 1755759600 * 1000, // 2025-08-21 09:00:00
  //stop: today.getTime(),
  stop: 1755802800 * 1000, // 2025-08-21 21:00:00
  window: '15m',
  unit: 'minute',
  stepSize: 60,
};
/*const optionDateRange = [
  {
    id: -1,
    display_text: 'VUE DETAILLEE',
    start: 1752666000 * 1000,
    stop: (1752666900) * 1000,
    window: '15s',
    unit: 'minute',
    stepSize: 2,
  },
];*/

const liveDateRange = {
  id: 1,
  start: now.getTime() - 5 * 60 * 1000,
  stop: now.getTime(),
  window: '10s',
  unit: 'second',
  stepSize: 15,
};

/* More info about charts used in this dashboard:
 * https://www.chartjs.org/docs/latest/
 */
const getOptions = (max=10, title='REBA Score', dateRange=0, onZoom=null, phases=[]) => { return {
  maintainAspectRatio: false,
  onClick: (evt, elements, chart) => {
    if (!elements?.length || !onZoom) return;
    const { index, datasetIndex } = elements[0];
    const ds = chart.data.datasets?.[datasetIndex]?.data || [];
    if (!ds.length) return;

    // x au format { x: timestamp(ms), y: ... }
    const xCurr = ds[index]?.x;
    if (!Number.isFinite(xCurr)) return;

    const xPrev = ds[index - 1]?.x;
    const xNext = ds[index + 1]?.x;

    // bornes via milieux des segments adjacents
    const left  = Number.isFinite(xPrev) ? Math.round((xPrev + xCurr) / 2) : (Number.isFinite(xNext) ? Math.round(xCurr - (xNext - xCurr) / 2) : xCurr - 1000);
    const right = Number.isFinite(xNext) ? Math.round((xCurr + xNext) / 2) : (Number.isFinite(xPrev) ? Math.round(xCurr + (xCurr - xPrev) / 2) : xCurr + 1000);

    // sécurité: au moins 1 ms d’écart
    const start = Math.min(left, right);
    const end   = Math.max(left, right + (right === left ? 1 : 0));

    onZoom(start, end);
  },
  scales: {
    x: {
      type: 'time',
      position: 'bottom',
      offset: false,
      grid: {
        display: true,
      },
      time: {
        unit: dateRange.unit,
        displayFormats: { hour: 'MMM D, YYYY HH:mm', minute: 'HH:mm' },
      },
      ticks: {
        stepSize: dateRange.stepSize,
      },
      min: dateRange.start,
      max: dateRange.stop,
      title: {
        display: false,
        text: 'Time',
      },
    },
    y: {
      min: 0,
      // max: max,
      beginAtZero: true,
      grid: {
        display: true,
      },
      title: {
        display: true,
        text: title,
      },
      ticks: {
        stepSize: 2,
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    zoom: {
      pan: false,
      zoom: {
        mode: 'x',
        drag: { enabled: true, backgroundColor: 'rgba(65, 100, 253,0.5)' },
        onZoomComplete: ({ chart }) => {
          const start = chart.scales.x.min;
          const end = chart.scales.x.max;
          console.log(onZoom, start, end);
          if (onZoom !== null)
            onZoom(start, end);
        },
      },
    },
    annotation: { annotations: phases },
  },
}; };

const donutOptions = {
  cutout: '80%',
  plugins: {
    legend: false,
  },
};

export const ErgonomicsPage = () => {

  const { workstationSelected } = useOrgData();
  const filters = useGetData('ergonomics_filters', 'ergonomics_filters');

  const [fields, setFields] = useState([]);
  const [checkboxes, setCheckboxes] = useState({});
  const [data, setData] = useState([]);
  const [expositionData, setExpositionData] = useState([]);
  const isLiveMode = new URLSearchParams(useLocation().search).has('live');
  const [selectedDateRange, setSelectedDateRange] = useState(isLiveMode ? liveDateRange : defaultDateRange);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (filters.isLoading || filters.isError) return;

    // Manage fields
    setFields([...REBA_FIELDS, ...filters.data.map((filter) => filter.name)]);

    // Manage checkboxes
    setCheckboxes((prev) => ({
      ...prev,
      ...filters.data.reduce((acc, filter) => {
        acc[filter.name] = false;
        return acc;
      }, {})
    }));
  }, [filters.isLoading, filters.isError]);

  useEffect( () => {
    let timer = null;

    const getData = async () => {
      if (!workstationSelected) return;

      setIsLoading(true);

      let newData = await getInfluxdbData(
        'engine',
        'reba_score',
        [],
        [],
        Math.floor(selectedDateRange.start / 1000),
        Math.floor(selectedDateRange.stop / 1000),
        null,
        null,
        // 'mean',
        null,
        [],
        null,
        getQuery(workstationSelected.id, fields, selectedDateRange.window),
        false,
        null,
      );
      newData = formatSeriesByField(newData, fields, -1);
      setData(newData);

      if (!isLiveMode) {
        // let newExpositionData = await getInfluxdbData(
        //   'engine',
        //   'reba_score',
        //   [],
        //   [],
        //   Math.floor(selectedDateRange.start / 1000),
        //   Math.floor(selectedDateRange.stop / 1000),
        //   null,
        //   null,
        //   null,
        //   [],
        //   null,
        //   getExpositionQuery(workstationSelected.id, REBA_THRESHOLDS_INFLUXDB),
        //   false,
        //   null,
        // );
        // setExpositionData(newExpositionData);
      }

      setIsLoading(false);
    };

    getData();

    if (isLiveMode) {
      timer = window.setInterval(() => {
        const nowLive = new Date();
        setSelectedDateRange({...selectedDateRange, start: nowLive.getTime() - 5 * 60 * 1000, stop: nowLive.getTime()})
      }, 10_000);
    }

    return () => {
      if (timer) window.clearInterval(timer);
    };
  }, [fields, selectedDateRange, workstationSelected]);

  const flags = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];

    const activeFilters = Object.keys(checkboxes).filter(key => checkboxes[key] && key in data);
    if (activeFilters.length === 0) return Object.values(data)[0]?.map(() => true) || [];

    return data[activeFilters[0]].map((_, i) =>
      activeFilters.every(key => {
        const v = data[key]?.[i]?.value;
        return v !== 0 || v === null;
      })
    );
  }, [data, checkboxes]);

  const filterPointsByFlags = (points) => {
    if(points === null || points === undefined) return [];

    return points.map((point, index) => {
      const timestamp = new Date(point.time).getTime();

      if (index < flags.length && flags[index]) {
        return { x: timestamp, y: point.value};
      } else {
        return { x: timestamp, y: 0 };
      }
    });
  };

  const [maxValues, meanValues] = useMemo(() => {
    const maxs = {};
    const means = {};

    for (let n = 1; n <= 13; n++) {
      const stepKey = `step_${n}`;

      if (stepKey in data) {
        let points = data[stepKey].map((point) => point.value);
        points = points.filter((point, index) => flags[index]);
        if (points.length > 0) {
          maxs[n] = Math.max(...points);
          means[n] = points.length ? points.reduce((a, b) => a + b, 0) / points.length : 0;
        }
        else{
          maxs[n] = 0;
          means[n] = 0;
        }
      }
    }

    return [maxs, means];
  }, [data, checkboxes]);

  const getColorClass = (value, warning_thresh, danger_thresh) => {
    if (value >= danger_thresh) return 'text-red-400 font-bold';
    else if (value >= warning_thresh) return 'text-yellow-400';
    else return 'text-green-400';
  };

  const getExpositionDuration = (key) => {
    const stepKey = `step_${key}`;
    const threshold = stepKey === 'step_13' ? REBA_THRESHOLDS[stepKey][2] : REBA_THRESHOLDS[stepKey][1];

    if (data[stepKey] === null || data[stepKey] === undefined) return 0;

    const points = data[stepKey].map((point) => point.value).filter((point, index) => flags[index]);
    if (points.length > 0) {
      return Math.round(points.filter((value) => value >= threshold).length / points.length * 100);
    }
    else{
      return 0;
    }
  };

  const handleZoom = (start, end) => {
    setSelectedDateRange(createDateRange(Math.round(start), Math.round(end)));
  };

  if (Object.keys(data).length === 0 || fields.length === 0) return <Loader />;

  const filtersDisplayText = Object.fromEntries(filters.data.map(o => [o.name, o.display_text]));
  const phases = createPhases(data, filtersDisplayText);

  return (
    <Panel category='main' title="" noPadding={true}>
      {
        isLoading && <Loader category="transparent"/>
      }
      <div className='h-full flex'>
        <div className='flex flex-col justify-between w-3/12 m-4 gap-4'>

          {/* Filters */}
          <ErgonomicsFilters
            checkboxes={checkboxes} setCheckboxes={setCheckboxes}
            flags={flags} filters={filters.data}
            selectedDateRange={selectedDateRange} setSelectedDateRange={setSelectedDateRange}
          />

          {/* RULA table */}
          <Panel title="SYNTHESE REBA" height="h-2/3">
            <div className='bg-perception-black-800'>
              <table className='w-full'>
                <thead>
                  <tr>
                    <th className='p-3 text-left bg-perception-gray-800'>Métrique</th>
                    <th className='p-3 text-left bg-perception-gray-800'>Valeur moyenne</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className='p-3'>Position de la nuque</td>
                    <td className={`p-3 ${getColorClass(meanValues[1], REBA_THRESHOLDS['step_1'][1], REBA_THRESHOLDS['step_1'][2])}`}>{Math.round(meanValues[1] * 10) / 10}</td>
                  </tr>
                  <tr>
                    <td className='p-3'>Position du tronc</td>
                    <td className={`p-3 ${getColorClass(meanValues[2], REBA_THRESHOLDS['step_2'][1], REBA_THRESHOLDS['step_2'][2])}`}>{Math.round(meanValues[2] * 10) / 10}</td>
                  </tr>
                  <tr>
                    <td className='p-3'>Position des jambes</td>
                    <td className={`p-3 ${getColorClass(meanValues[3], REBA_THRESHOLDS['step_3'][1], REBA_THRESHOLDS['step_3'][2])}`}>{Math.round(meanValues[3] * 10) / 10}</td>
                  </tr>
                  <tr>
                    <td className='p-3 font-bold'>Score de posture A</td>
                    <td className={`p-3 ${getColorClass(meanValues[4], REBA_THRESHOLDS['step_4'][1], REBA_THRESHOLDS['step_4'][2])}`}>{Math.round(meanValues[4] * 10) / 10}</td>
                  </tr>
                  <tr>
                    <td className='p-3'>Position de l'épaule</td>
                    <td className={`p-3 ${getColorClass(meanValues[7], REBA_THRESHOLDS['step_7'][1], REBA_THRESHOLDS['step_7'][2])}`}>{Math.round(meanValues[7] * 10) / 10}</td>
                  </tr>
                  <tr>
                    <td className='p-3'>Position du coude</td>
                    <td className={`p-3 ${getColorClass(meanValues[8], REBA_THRESHOLDS['step_8'][1], REBA_THRESHOLDS['step_8'][2])}`}>{Math.round(meanValues[8] * 10) / 10}</td>
                  </tr>
                  <tr>
                    <td className='p-3'>Position du poignet</td>
                    <td className={`p-3 ${getColorClass(meanValues[9], REBA_THRESHOLDS['step_9'][1], REBA_THRESHOLDS['step_9'][2])}`}>{Math.round(meanValues[9] * 10) / 10}</td>
                  </tr>
                  <tr>
                    <td className='p-3 font-bold'>Score de posture B</td>
                    <td className={`p-3 ${getColorClass(meanValues[10], REBA_THRESHOLDS['step_10'][1], REBA_THRESHOLDS['step_10'][2])}`}>{Math.round(meanValues[10] * 10) / 10}</td>
                  </tr>
                  <tr>
                    <td className='p-3 font-bold'>Score de posture C</td>
                    <td className={`p-3 ${getColorClass(meanValues[13], REBA_THRESHOLDS['step_13'][1], REBA_THRESHOLDS['step_13'][3])}`}>{Math.round(meanValues[13] * 10) / 10}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Panel>
        </div>

        {/* Graphs */}
        <div className='flex flex-col w-7/12 m-4 ml-0 gap-4'>
          <Panel title="DONNEES OBSERVEES - EVOLUTION SCORES TABLE A / TABLE B / TABLE C" height="full">
            <div className='h-full flex flex-col gap-4'>
              {/* Table C */}
              <div className='flex h-2/3'>
                <div className='w-full'>
                  <Bar data={{label: 'Score Table C', datasets: [{
                      data: filterPointsByFlags(data['step_13']),
                      borderColor: (context) => getBarColor(context, 'step_13'),
                      backgroundColor: (context) => getBarColor(context, 'step_13'),
                      pointStyle: false,
                      tension: 0.3,
                      fill: true,
                    }]}} options={getOptions(12, 'Table C', selectedDateRange, handleZoom, phases)}/>
                </div>
                {
                  !isLiveMode &&
                    <div className='flex flex-col items-center justify-center w-1/6'>
                      <p className='mb-2 text-xs'>Taux d'exposition</p>
                      <div className='size-16 relative'>
                        <Doughnut data={{
                          datasets: [
                            {
                              data: [getExpositionDuration(13), 100 - getExpositionDuration(13)],
                              backgroundColor: (context) => [getExpositionColor(context), 'rgba(67, 67, 67, 1)'],
                              borderWidth: 0,
                            },
                          ],
                        }} options={donutOptions} />
                        <div className='absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2'>{getExpositionDuration(13)}%</div>
                      </div>
                    </div>
                }
              </div>

              {/* Table A */}
              <div className='flex h-1/3'>
                <div className='w-full'>
                  <Bar data={{label: 'Score Table A', datasets: [{
                      data: filterPointsByFlags(data['step_4']),
                      borderColor: (context) => getBarColor(context, 'step_4'),
                      backgroundColor: (context) => getBarColor(context, 'step_4'),
                      pointStyle: false,
                      tension: 0.3,
                      fill: true,
                    }]}} options={getOptions(9, 'Table A', selectedDateRange, handleZoom, phases)}/>
                </div>
                {
                  !isLiveMode &&
                    <div className='flex flex-col items-center justify-center w-1/6'>
                      <p className='mb-2 text-xs'>Taux d'exposition</p>
                      <div className='size-16 relative'>
                        <Doughnut data={{
                          datasets: [
                            {
                              data: [getExpositionDuration(4), 100 - getExpositionDuration(4)],
                              backgroundColor: (context) => [getExpositionColor(context), 'rgba(67, 67, 67, 1)'],
                              borderWidth: 0,
                            },
                          ],
                        }} options={donutOptions} />
                        <div className='absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2'>{getExpositionDuration(4)}%</div>
                      </div>
                    </div>
                }
              </div>

              {/* Table B */}
              <div className='flex h-1/3'>
                <div className='w-full'>
                  <Bar data={{label: 'Score Table B', datasets: [{
                      data: filterPointsByFlags(data['step_10']),
                      borderColor: (context) => getBarColor(context, 'step_10'),
                      backgroundColor: (context) => getBarColor(context, 'step_10'),
                      pointStyle: false,
                      tension: 0.3,
                      fill: true,
                    }]}} options={getOptions(9, 'Table B', selectedDateRange, handleZoom, phases)}/>
                </div>
                {
                  !isLiveMode &&
                    <div className='flex flex-col items-center justify-center w-1/6'>
                      <p className='mb-2 text-xs'>Taux d'exposition</p>
                      <div className='size-16 relative'>
                        <Doughnut data={{
                          datasets: [
                            {
                              data: [getExpositionDuration(10), 100 - getExpositionDuration(10)],
                              backgroundColor: (context) => [getExpositionColor(context), 'rgba(67, 67, 67, 1)'],
                              borderWidth: 0,
                            },
                          ],
                        }} options={donutOptions} />
                        <div className='absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2'>{getExpositionDuration(10)}%</div>
                      </div>
                    </div>
                }
              </div>
            </div>
          </Panel>

          <Panel title="DONNEES OBSERVEES - DETAIL DES SCORES" height="full">
            <div className='h-full flex flex-col gap-4'>
              {/* Other tables */}
              <div className='flex h-1/3'>
                <div className='w-full'>
                  <Bar data={{label: 'Score 1', datasets: [{
                      data: filterPointsByFlags(data['step_1']),
                      borderColor: (context) => getBarColor(context, 'step_1'),
                      backgroundColor: (context) => getBarColor(context, 'step_1'),
                      pointStyle: false,
                      tension: 0.3,
                      fill: true,
                    }]}} options={getOptions(3, 'Nuque', selectedDateRange, handleZoom, phases)}/>
                </div>
                {
                  !isLiveMode &&
                    <div className='flex flex-col items-center justify-center w-1/6'>
                      <p className='mb-2 text-xs'>Taux d'exposition</p>
                      <div className='size-16 relative'>
                        <Doughnut data={{
                          datasets: [
                            {
                              data: [getExpositionDuration(1), 100 - getExpositionDuration(1)],
                              backgroundColor: (context) => [getExpositionColor(context), 'rgba(67, 67, 67, 1)'],
                              borderWidth: 0,
                            },
                          ],
                        }} options={donutOptions} />
                        <div className='absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2'>{getExpositionDuration(1)}%</div>
                      </div>
                    </div>
                }
              </div>
              <div className='flex h-1/3'>
                <div className='w-full'>
                  <Bar data={{label: 'Score 2', datasets: [{
                      data: filterPointsByFlags(data['step_2']),
                      borderColor: (context) => getBarColor(context, 'step_2'),
                      backgroundColor: (context) => getBarColor(context, 'step_2'),
                      pointStyle: false,
                      tension: 0.3,
                      fill: true,
                    }]}} options={getOptions(5, 'Tronc', selectedDateRange, handleZoom, phases)}/>
                </div>
                {
                  !isLiveMode &&
                    <div className='flex flex-col items-center justify-center w-1/6'>
                      <p className='mb-2 text-xs'>Taux d'exposition</p>
                      <div className='size-16 relative'>
                        <Doughnut data={{
                          datasets: [
                            {
                              data: [getExpositionDuration(2), 100 - getExpositionDuration(2)],
                              backgroundColor: (context) => [getExpositionColor(context), 'rgba(67, 67, 67, 1)'],
                              borderWidth: 0,
                            },
                          ],
                        }} options={donutOptions} />
                        <div className='absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2'>{getExpositionDuration(2)}%</div>
                      </div>
                    </div>
                }
              </div>
              <div className='flex h-1/3'>
                <div className='w-full'>
                  <Bar data={{label: 'Score 3', datasets: [{
                      data: filterPointsByFlags(data['step_3']),
                      borderColor: (context) => getBarColor(context, 'step_3'),
                      backgroundColor: (context) => getBarColor(context, 'step_3'),
                      pointStyle: false,
                      tension: 0.3,
                      fill: true,
                    }]}} options={getOptions(4, 'Jambes', selectedDateRange, handleZoom, phases)}/>
                </div>
                {
                  !isLiveMode &&
                    <div className='flex flex-col items-center justify-center w-1/6'>
                      <p className='mb-2 text-xs'>Taux d'exposition</p>
                      <div className='size-16 relative'>
                        <Doughnut data={{
                          datasets: [
                            {
                              data: [getExpositionDuration(3), 100 - getExpositionDuration(3)],
                              backgroundColor: (context) => [getExpositionColor(context), 'rgba(67, 67, 67, 1)'],
                              borderWidth: 0,
                            },
                          ],
                        }} options={donutOptions} />
                        <div className='absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2'>{getExpositionDuration(3)}%</div>
                      </div>
                    </div>
                }
              </div>
              <div className='flex h-1/3'>
                <div className='w-full'>
                  <Bar data={{label: 'Score 7', datasets: [{
                      data: filterPointsByFlags(data['step_7']),
                      borderColor: (context) => getBarColor(context, 'step_7'),
                      backgroundColor: (context) => getBarColor(context, 'step_7'),
                      pointStyle: false,
                      tension: 0.3,
                      fill: true,
                    }]}} options={getOptions(6, 'Epaule', selectedDateRange, handleZoom, phases)}/>
                </div>
                {
                  !isLiveMode &&
                    <div className='flex flex-col items-center justify-center w-1/6'>
                      <p className='mb-2 text-xs'>Taux d'exposition</p>
                      <div className='size-16 relative'>
                        <Doughnut data={{
                          datasets: [
                            {
                              data: [getExpositionDuration(7), 100 - getExpositionDuration(7)],
                              backgroundColor: (context) => [getExpositionColor(context), 'rgba(67, 67, 67, 1)'],
                              borderWidth: 0,
                            },
                          ],
                        }} options={donutOptions} />
                        <div className='absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2'>{getExpositionDuration(7)}%</div>
                      </div>
                    </div>
                }
              </div>
              <div className='flex h-1/3'>
                <div className='w-full'>
                  <Bar data={{label: 'Score 8', datasets: [{
                      data: filterPointsByFlags(data['step_8']),
                      borderColor: (context) => getBarColor(context, 'step_8'),
                      backgroundColor: (context) => getBarColor(context, 'step_8'),
                      pointStyle: false,
                      tension: 0.3,
                      fill: true,
                    }]}} options={getOptions(2, 'Coude', selectedDateRange, handleZoom, phases)}/>
                </div>
                {
                  !isLiveMode &&
                    <div className='flex flex-col items-center justify-center w-1/6'>
                      <p className='mb-2 text-xs'>Taux d'exposition</p>
                      <div className='size-16 relative'>
                        <Doughnut data={{
                          datasets: [
                            {
                              data: [getExpositionDuration(8), 100 - getExpositionDuration(8)],
                              backgroundColor: (context) => [getExpositionColor(context), 'rgba(67, 67, 67, 1)'],
                              borderWidth: 0,
                            },
                          ],
                        }} options={donutOptions} />
                        <div className='absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2'>{getExpositionDuration(8)}%</div>
                      </div>
                    </div>
                }
              </div>
              <div className='flex h-1/3'>
                <div className='w-full'>
                  <Bar data={{label: 'Score 9', datasets: [{
                      data: filterPointsByFlags(data['step_9']),
                      borderColor: (context) => getBarColor(context, 'step_9'),
                      backgroundColor: (context) => getBarColor(context, 'step_9'),
                      pointStyle: false,
                      tension: 0.3,
                      fill: true,
                    }]}} options={getOptions(3, 'Poignet', selectedDateRange, handleZoom, phases)}/>
                </div>
                {
                  !isLiveMode &&
                    <div className='flex flex-col items-center justify-center w-1/6'>
                      <p className='mb-2 text-xs'>Taux d'exposition</p>
                      <div className='size-16 relative'>
                        <Doughnut data={{
                          datasets: [
                            {
                              data: [getExpositionDuration(9), 100 - getExpositionDuration(9)],
                              backgroundColor: (context) => [getExpositionColor(context), 'rgba(67, 67, 67, 1)'],
                              borderWidth: 0,
                            },
                          ],
                        }} options={donutOptions} />
                        <div className='absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2'>{getExpositionDuration(9)}%</div>
                      </div>
                    </div>
                }
              </div>
            </div>
          </Panel>
        </div>

        {/* Notifications */}
        <div className="w-2/12 m-4 ml-0">
          <ErgonomicsNotifications />
        </div>
      </div>
    </Panel>
  );
};
